import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, ArrowUp, ArrowDown, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiErr } from "@/lib/api";
import { ImageField, Field } from "./ImageField";

const SPANS = [
  { value: "lg", label: "Tall (4:5)" },
  { value: "md", label: "Portrait (3:4)" },
  { value: "sm", label: "Square" },
  { value: "wide", label: "Wide (4:3)" },
];

export const GalleryTab = () => {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () =>
    api
      .get("/gallery")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(apiErr(e)));

  useEffect(() => {
    load();
  }, []);

  const patchItem = async (item, changes) => {
    const updated = { ...item, ...changes };
    setItems((prev) => prev.map((g) => (g.id === item.id ? updated : g)));
    try {
      await api.put(`/gallery/${item.id}`, updated);
      toast.success("Photo updated");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const move = async (index, dir) => {
    const next = [...items];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
    try {
      await api.post("/gallery/reorder", { ids: next.map((g) => g.id) });
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const addNew = async () => {
    if (!adding.src) {
      toast.error("Please upload a photo first");
      return;
    }
    setSaving(true);
    try {
      await api.post("/gallery", adding);
      toast.success("Photo added to gallery");
      setAdding(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/gallery/${id}`);
      setItems((prev) => prev.filter((g) => g.id !== id));
      setConfirmDelete(null);
      toast.success("Photo removed");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  if (!items) return <Loader2 className="mx-auto mt-20 h-6 w-6 animate-spin text-saffron" />;

  return (
    <div data-testid="admin-gallery">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-ink">Gallery</h1>
          <p className="mt-1 font-body text-sm text-steel">
            {items.length} photos · use the arrows to arrange the order shown on the site
          </p>
        </div>
        <button
          data-testid="gallery-add-btn"
          onClick={() => setAdding({ src: "", caption: "", span: "sm" })}
          className="inline-flex items-center gap-2 rounded-full bg-saffron px-5 py-2.5 font-heading text-sm font-semibold text-white transition-colors hover:bg-saffron-soft"
        >
          <Plus className="h-4 w-4" /> Add Photo
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((g, i) => (
          <div key={g.id} data-testid={`gallery-row-${g.id}`} className="overflow-hidden rounded-xl border border-steel/20 bg-aero-surface">
            <div className="relative aspect-[16/10] bg-white">
              <img src={g.src} alt={g.caption} className="h-full w-full object-cover" />
              <div className="absolute right-2 top-2 flex gap-1">
                <button
                  data-testid={`gallery-move-up-${g.id}`}
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className="grid h-8 w-8 place-items-center rounded-full bg-aero-navy/90 text-ink transition-colors hover:text-saffron disabled:opacity-40"
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button
                  data-testid={`gallery-move-down-${g.id}`}
                  onClick={() => move(i, 1)}
                  disabled={i === items.length - 1}
                  className="grid h-8 w-8 place-items-center rounded-full bg-aero-navy/90 text-ink transition-colors hover:text-saffron disabled:opacity-40"
                >
                  <ArrowDown className="h-4 w-4" />
                </button>
              </div>
              <span className="absolute left-2 top-2 rounded-full bg-aero-navy/90 px-2.5 py-1 font-heading text-[10px] font-bold text-saffron">
                #{i + 1}
              </span>
            </div>
            <div className="space-y-3 p-4">
              <CaptionEditor item={g} onSave={(caption) => patchItem(g, { caption })} />
              <div className="flex items-center justify-between gap-2">
                <select
                  data-testid={`gallery-span-${g.id}`}
                  value={g.span}
                  onChange={(e) => patchItem(g, { span: e.target.value })}
                  className="rounded-lg border border-steel/25 bg-white px-2.5 py-1.5 font-body text-xs text-ink outline-none focus:border-saffron"
                >
                  {SPANS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                {confirmDelete === g.id ? (
                  <button
                    data-testid={`gallery-confirm-delete-${g.id}`}
                    onClick={() => remove(g.id)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1.5 font-heading text-xs font-semibold text-white"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Confirm?
                  </button>
                ) : (
                  <button
                    data-testid={`gallery-delete-${g.id}`}
                    onClick={() => setConfirmDelete(g.id)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-3 py-1.5 font-heading text-xs font-semibold text-red-500 transition-colors hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!adding} onOpenChange={(o) => !o && setAdding(null)}>
        <DialogContent data-testid="gallery-add-dialog" className="max-w-lg border-steel/25 bg-aero-surface text-ink">
          {adding && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl font-bold text-ink">Add Gallery Photo</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <ImageField label="Photo" value={adding.src} testId="gallery-new-image" onChange={(v) => setAdding({ ...adding, src: v })} />
                <Field label="Caption" value={adding.caption} testId="gallery-new-caption" onChange={(v) => setAdding({ ...adding, caption: v })} />
                <div>
                  <label className="mb-1.5 block font-heading text-xs font-semibold uppercase tracking-wide text-steel">Shape</label>
                  <select
                    data-testid="gallery-new-span"
                    value={adding.span}
                    onChange={(e) => setAdding({ ...adding, span: e.target.value })}
                    className="w-full rounded-lg border border-steel/25 bg-white px-3 py-2 font-body text-sm text-ink outline-none focus:border-saffron"
                  >
                    {SPANS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  data-testid="gallery-save-btn"
                  onClick={addNew}
                  disabled={saving}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-saffron py-3 font-heading text-sm font-semibold text-white transition-colors hover:bg-saffron-soft disabled:opacity-60"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Add to Gallery
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CaptionEditor = ({ item, onSave }) => {
  const [value, setValue] = useState(item.caption);
  const dirty = value !== item.caption;
  return (
    <div className="flex gap-2">
      <input
        data-testid={`gallery-caption-${item.id}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Caption"
        className="w-full rounded-lg border border-steel/25 bg-white px-3 py-1.5 font-body text-xs text-ink outline-none focus:border-saffron"
      />
      {dirty && (
        <button
          data-testid={`gallery-caption-save-${item.id}`}
          onClick={() => onSave(value)}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-saffron text-white"
          title="Save caption"
        >
          <Check className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
